import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { coordinatorTasksAPI, usersAPI } from '../services/api';
import { CheckCircle, Circle, RotateCcw, User, MinusCircle } from 'lucide-react';

const TASK_TYPES = [
  { key: 'drawing', label: 'Drawing' },
  { key: 'cutting_list', label: 'Cutting List' },
  { key: 'recheck', label: 'Re-check' },
];

const CoordinatorTaskManager = ({ jobCard, coordinatorName, onUpdate }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState({});
  const [allCoordinators, setAllCoordinators] = useState([]);
  const clientName = jobCard?.customer_name || '';

  useEffect(() => {
    const fetchCoordinators = async () => {
      try {
        const users = await usersAPI.getAll('coordinator');
        setAllCoordinators(users || []);
      } catch {
        // Fallback: at least show the current coordinator
        setAllCoordinators([]);
      }
    };
    fetchCoordinators();
  }, []);

  const tasks = useMemo(() => {
    const taskMap = {};
    TASK_TYPES.forEach(t => {
      taskMap[t.key] = { task_type: t.key, status: 'pending', performed_by: null, performed_by_role: null };
    });
    if (jobCard?.coordinator_tasks) {
      jobCard.coordinator_tasks.forEach(t => {
        if (t.task_type) {
          taskMap[t.task_type] = t;
        }
      });
    }
    return taskMap;
  }, [jobCard]);

  // A task counts as resolved (unblocks send-to-supervisor) when completed OR marked N/A
  const resolvedCount = TASK_TYPES.filter(t => ['completed', 'na'].includes(tasks[t.key]?.status)).length;

  const handleAssignTask = async (taskType, coordinatorValue) => {
    setLoading(prev => ({ ...prev, [taskType]: true }));
    try {
      // coordinatorValue format: "coordinator:Name", "client:Name", "intern:Name", or "na:"
      const [role, ...nameParts] = coordinatorValue.split(':');
      const name = nameParts.join(':');

      await coordinatorTasksAPI.updateTask(jobCard.id, taskType, name, role);
      toast({ title: "Success", description: role === 'na' ? 'Task marked N/A' : `Task assigned to ${name}` });
      if (onUpdate) onUpdate();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to assign task", variant: "destructive" });
    } finally {
      setLoading(prev => ({ ...prev, [taskType]: false }));
    }
  };

  const handleResetTask = async (taskType) => {
    setLoading(prev => ({ ...prev, [taskType]: true }));
    try {
      await coordinatorTasksAPI.resetTask(jobCard.id, taskType);
      toast({ title: "Success", description: "Task reset to pending" });
      if (onUpdate) onUpdate();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to reset task", variant: "destructive" });
    } finally {
      setLoading(prev => ({ ...prev, [taskType]: false }));
    }
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-sm flex items-center gap-2">
            <User className="w-4 h-4" />
            Coordinator Tasks
          </h4>
          <Badge variant="outline" className="text-xs" data-testid="task-progress">
            {resolvedCount}/{TASK_TYPES.length}
          </Badge>
        </div>

        <div className="space-y-3">
          {TASK_TYPES.map(taskDef => {
            const task = tasks[taskDef.key];
            const isCompleted = task?.status === 'completed';
            const isNa = task?.status === 'na';
            const isResolved = isCompleted || isNa;
            const isLoading = loading[taskDef.key];

            return (
              <div key={taskDef.key} className={`flex items-center justify-between p-2 rounded-lg border ${isCompleted ? 'bg-green-50 border-green-200' : isNa ? 'bg-slate-100 border-slate-200' : 'bg-gray-50 border-gray-200'}`} data-testid={`task-${taskDef.key}`}>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {isCompleted ? (
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                  ) : isNa ? (
                    <MinusCircle className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  ) : (
                    <Circle className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  )}
                  <div className="min-w-0">
                    <p className={`text-sm font-medium ${isNa ? 'text-slate-500' : ''}`}>{taskDef.label}</p>
                    {isCompleted && task.performed_by && (
                      <p className="text-xs text-green-700 truncate">
                        by {task.performed_by}
                        {task.performed_by_role === 'client' && (
                          <span className="ml-1 inline-block text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">client</span>
                        )}
                      </p>
                    )}
                    {isNa && (
                      <p className="text-xs text-slate-500 truncate">Not applicable</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-1 flex-shrink-0">
                  {isResolved ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-gray-500 hover:text-red-600"
                      onClick={() => handleResetTask(taskDef.key)}
                      disabled={isLoading}
                      data-testid={`reset-task-${taskDef.key}`}
                    >
                      <RotateCcw className="w-3 h-3" />
                    </Button>
                  ) : (
                    <Select
                      onValueChange={(value) => handleAssignTask(taskDef.key, value)}
                      disabled={isLoading}
                    >
                      <SelectTrigger className="h-7 w-[140px] text-xs" data-testid={`assign-task-${taskDef.key}`}>
                        <SelectValue placeholder="Assign to..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem
                          key="__na__"
                          value="na:"
                          data-testid={`assign-na-${taskDef.key}`}
                        >
                          N/A (not applicable)
                        </SelectItem>
                        {clientName && (
                          <SelectItem
                            key="__client__"
                            value={`client:${clientName}`}
                            data-testid={`assign-client-${taskDef.key}`}
                          >
                            Client ({clientName})
                          </SelectItem>
                        )}
                        {allCoordinators.length > 0 ? (
                          allCoordinators.map(c => (
                            <SelectItem key={c.id} value={`coordinator:${c.name}`}>
                              {c.name}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value={`coordinator:${coordinatorName}`}>
                            {coordinatorName}
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default CoordinatorTaskManager;
